import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Alert,
  AlertIcon,
  Divider,
  Link,
  Icon,
  useColorModeValue,
  useToast,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  SimpleGrid,
  Flex
} from '@chakra-ui/react';
import { CheckCircleIcon, DownloadIcon, EmailIcon, CheckIcon, InfoIcon } from '@chakra-ui/icons';
import { FaBoxOpen, FaShippingFast, FaHome, FaCheck } from 'react-icons/fa';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { sendDownloadLinksEmail, sendOrderConfirmationEmail } from '../utils/emailUtils';
import { useAuth } from '../context/AuthContext';
import { markItemsAsPurchased } from '../utils/stripeUtils';

const OrderSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [orderDetails, setOrderDetails] = useState(null);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sentConfirmation, setSentConfirmation] = useState(false);
  const { user } = useAuth();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const highlightColor = useColorModeValue('green.50', 'green.900');
  
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        
        // First try to get order data from location state
        let orderData = location.state?.orderData;
        
        // If not available, try localStorage
        if (!orderData) {
          const storedOrder = localStorage.getItem('last_order_data');
          if (storedOrder) {
            try {
              orderData = JSON.parse(storedOrder);
              console.log('Retrieved order data from localStorage:', orderData);
            } catch (e) {
              console.error('Error parsing order data from localStorage:', e);
            }
          }
        }
        
        // Also check URL params for order_success and item IDs
        const urlParams = new URLSearchParams(location.search);
        const orderSuccess = urlParams.get('order_success');
        const orderItems = urlParams.get('items');
        
        console.log('URL parameters:', { orderSuccess, orderItems });
        
        // If we have order data, use it
        if (orderData) {
          setOrderDetails({
            id: orderData.id,
            paymentIntentId: orderData.payment_intent_id,
            total: orderData.amount,
            date: new Date(orderData.timestamp).toLocaleDateString(),
            package: orderData.package?.name || 'Custom Package',
            itemCount: orderData.items?.length || 0,
            status: orderData.status || 'succeeded',
            email: user?.email,
            isDigital: orderData.requires_shipping === false,
            shippingAddress: orderData.shipping_address,
            items: orderData.items || []
          });
          
          // Set download links for digital items
          if (orderData.items) {
            // For each item, fetch from exports table to get the actual export_url
            try {
              const itemIds = orderData.items.map(item => item.id).filter(Boolean);
              if (itemIds.length > 0) {
                const { data: exportItems } = await supabase
                  .from('exports')
                  .select('id, name, export_url')
                  .in('id', itemIds);
                
                if (exportItems && exportItems.length > 0) {
                  // Create a map for quick lookup
                  const exportMap = {};
                  exportItems.forEach(item => {
                    exportMap[item.id] = item;
                  });
                  
                  // Set download links with export_url when available
                  setDownloadLinks(
                    orderData.items.map(item => {
                      const exportItem = exportMap[item.id];
                      return {
                        id: item.id,
                        name: item.name || exportItem?.name || 'Custom design',
                        url: exportItem?.export_url || item.url || item.file_url || '#'
                      };
                    })
                  );
                } else {
                  // Fallback to original data if exports not found
                  setDownloadLinks(
                    orderData.items.map(item => ({
                      id: item.id,
                      name: item.name || 'Custom design',
                      url: item.url || item.file_url || '#'
                    }))
                  );
                }
              }
            } catch (exportError) {
              console.warn('Error fetching export data:', exportError);
              // Fallback to original data
              setDownloadLinks(
                orderData.items.map(item => ({
                  id: item.id,
                  name: item.name || 'Custom design',
                  url: item.url || item.file_url || '#'
                }))
              );
            }
          }
          
          // Mark items as purchased in database if needed
          if (orderData.itemIds && orderData.itemIds.length > 0) {
            try {
              const result = await markItemsAsPurchased(orderData.itemIds);
              console.log('Marked items as purchased:', result);
              
              // Send order confirmation email
              if (user?.email && !sentConfirmation) {
                try {
                  // Make sure we have the correct total amount before sending email
                  const emailOrderData = {
                    ...orderData,
                    total: orderData.amount || orderData.total || 0,
                    orderNumber: orderData.id,
                    orderDate: orderData.timestamp || new Date().toISOString()
                  };
                  
                  await sendOrderConfirmationEmail(emailOrderData, user.email);
                  setSentConfirmation(true);
                  console.log('Order confirmation email sent via Resend');
                } catch (emailError) {
                  console.warn('Failed to send order confirmation email:', emailError);
                }
              }
            } catch (markError) {
              console.warn('Error marking items as purchased:', markError);
            }
          }
        } else if (orderSuccess === 'true' && orderItems) {
          // We have some URL parameters but no order data
          // This is a limited fallback mode
          const itemIds = orderItems.split(',').filter(Boolean);
          
          if (itemIds.length > 0) {
            toast({
              title: 'Order completed',
              description: `Your payment was successful. ${itemIds.length} items purchased.`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            
            // Create minimal order details
            setOrderDetails({
              id: 'unknown-' + Date.now(),
              paymentIntentId: 'unknown',
              total: 0,
              date: new Date().toLocaleDateString(),
              package: 'Custom Package',
              itemCount: itemIds.length,
              status: 'succeeded',
              email: user?.email,
              isDigital: true,
              items: []
            });
            
            // Mark the items as purchased
            try {
              const { data, error } = await supabase
                .from('exports')
                .update({ is_purchased: true })
                .in('id', itemIds)
                .eq('status', 'active')
                .select();
              
              if (data && data.length > 0) {
                setOrderDetails(prev => ({
                  ...prev,
                  items: data
                }));
                
                setDownloadLinks(
                  data.map(item => ({
                    id: item.id,
                    name: item.name || 'Custom design',
                    url: item.export_url || '#'
                  }))
                );
              }
            } catch (fetchError) {
              console.error('Error fetching purchased items:', fetchError);
            }
          } else {
            throw new Error('No valid item IDs found in URL parameters');
          }
        } else {
          throw new Error('No payment information found');
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        toast({
          title: 'Error',
          description: 'Could not retrieve your order details. Please contact support.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        
        // Redirect to dashboard after showing toast
        setTimeout(() => navigate('/dashboard'), 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [location, navigate, toast, user, sentConfirmation]);
  
  const handleDownload = (link) => {
    try {
      // Create a download link
      const downloadLink = document.createElement('a');
      downloadLink.href = link.url;
      downloadLink.download = link.name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      toast({
        title: 'Download started',
        description: `Downloading ${link.name}`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: 'Could not download file. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const handleSendEmail = async () => {
    if (!orderDetails || !user?.email) {
      toast({
        title: 'Cannot send email',
        description: 'Missing order details or email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      setLoading(true);
      
      // Send the download links email
      await sendDownloadLinksEmail(
        downloadLinks,
        user.email,
        orderDetails.id
      );
      
      toast({
        title: 'Email sent',
        description: `Download links sent to ${user.email}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Error sending email',
        description: error.message || 'There was an error sending the email',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Shipping status steps
  const renderShippingStatus = () => {
    // Determine current step (in a real app, this would come from the database)
    // For demo purposes, we'll use a fixed step
    const currentStep = 1; // 0: Processing, 1: Shipped, 2: Delivered
    
    const steps = [
      { title: 'Processing', description: 'Order is being processed', icon: FaBoxOpen },
      { title: 'Shipped', description: 'Package is on the way', icon: FaShippingFast },
      { title: 'Delivered', description: 'Package has been delivered', icon: FaHome }
    ];
    
    return (
      <Stepper index={currentStep} colorScheme="green" size="sm" mt={4}>
        {steps.map((step, index) => (
          <Step key={index}>
            <StepIndicator>
              <StepStatus 
                complete={<Icon as={FaCheck} />} 
                incomplete={<Icon as={step.icon} />} 
                active={<Icon as={step.icon} />} 
              />
            </StepIndicator>
            <Box flexShrink="0">
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </Box>
            <StepSeparator />
          </Step>
        ))}
      </Stepper>
    );
  };
  
  if (loading) {
    return (
      <Container maxW="container.md" py={10} textAlign="center">
        <Text>Loading your order details...</Text>
      </Container>
    );
  }

  if (!orderDetails) {
    return (
      <Container maxW="container.md" py={10} textAlign="center">
        <Heading size="lg" mb={4}>Order Details Not Found</Heading>
        <Text mb={6}>We couldn't find your order information. Please check your email for order confirmation.</Text>
        <Button as={RouterLink} to="/dashboard" colorScheme="blue">
          Return to Dashboard
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxW="container.lg" py={10}>
      <Box
        bg={bgColor}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        p={6}
        boxShadow="md"
        mb={8}
      >
        <Flex direction="column" align="center" textAlign="center" mb={6}>
          <Icon as={CheckCircleIcon} w={16} h={16} color="green.500" mb={4} />
          <Heading size="xl">Thank You for Your Order!</Heading>
          <Text fontSize="lg" mt={2}>
            Your payment has been processed successfully.
          </Text>
          {orderDetails.email && (
            <Text mt={2} color="gray.600">
              A confirmation email has been sent to {orderDetails.email}
            </Text>
          )}
        </Flex>

        <Divider my={6} />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box>
            <Heading size="md" mb={3}>Order Summary</Heading>
            <VStack align="stretch" spacing={2}>
              <Flex justify="space-between">
                <Text>Order ID:</Text>
                <Text fontWeight="bold">{orderDetails.id}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text>Date:</Text>
                <Text>{orderDetails.date}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text>Package:</Text>
                <Text>{orderDetails.package}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text>Items:</Text>
                <Text>{orderDetails.itemCount}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text>Total:</Text>
                <Text fontWeight="bold">${orderDetails.total?.toFixed(2)}</Text>
              </Flex>
              <Flex justify="space-between">
                <Text>Status:</Text>
                <Badge colorScheme="green">{orderDetails.status}</Badge>
              </Flex>
              <Flex justify="space-between">
                <Text>Type:</Text>
                <Badge colorScheme={orderDetails.isDigital ? "blue" : "orange"}>
                  {orderDetails.isDigital ? "Digital" : "Physical"}
                </Badge>
              </Flex>
            </VStack>
          </Box>

          <Box>
            {orderDetails.isDigital ? (
              <>
                <Heading size="md" mb={3}>Your Downloads</Heading>
                {downloadLinks.length > 0 ? (
                  <VStack align="stretch" spacing={3}>
                    {downloadLinks.map((item) => (
                      <Flex 
                        key={item.id}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        justify="space-between"
                        align="center"
                      >
                        <Text noOfLines={1}>{item.name}</Text>
                        <Button 
                          onClick={() => handleDownload(item)}
                          size="sm" 
                          rightIcon={<DownloadIcon />}
                          colorScheme="blue"
                        >
                          Download
                        </Button>
                      </Flex>
                    ))}
                    
                    <Button
                      leftIcon={<EmailIcon />}
                      colorScheme="teal"
                      variant="outline"
                      mt={4}
                      onClick={handleSendEmail}
                      isDisabled={!user?.email}
                    >
                      Email Me Download Links
                    </Button>
                  </VStack>
                ) : (
                  <Text>Your downloads will be ready shortly. Please check your email.</Text>
                )}
              </>
            ) : (
              <>
                <Heading size="md" mb={3}>Digital Downloads</Heading>
                <Text>
                  To download your purchased digital images click the button below to visit your Pro Designs page where you will be able to download your high resolution images. If you have any questions please email admin@playmakerpics.com. All printed frames will be delivered in 2-3 days to your local gym for pickup.
                </Text>
              </>
            )}
          </Box>
        </SimpleGrid>

        <Divider my={6} />

        <HStack spacing={4} justify="center">
          <Button as={RouterLink} to="/designs" colorScheme="green">
            Download Purchased Designs
          </Button>
        </HStack>
      </Box>
    </Container>
  );
};

export default OrderSuccessPage; 